import { Component, OnInit, Input, ChangeDetectionStrategy, OnDestroy, ChangeDetectorRef, Output, EventEmitter } from '@angular/core';
import { BookmarkModel } from '../../models/bookmark.model';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Store, Actions, ofActionDispatched } from '@ngxs/store';
import {
  DeleteBookmark,
  DeleteBookmarkSuccess,
  DeleteBookmarkFail,
  DeleteFavoriteSuccess,
  DeleteFavoriteFail,
  DeleteFavorite,
  CopyBookmark,
  CopyBookmarkSuccess,
  CopyBookmarkFailure
} from '../../state/bookmarks.actions';
import { PublishDialogComponent, PublishDialogData } from 'src/app/publish/components/publish-dialog/publish-dialog.component';
import { GetPublishURLSuccess, GetPublishURL, GetPublishURLFail } from 'src/app/publish/state/publish.actions';
import { WidgetOptions } from 'src/app/publish/models/widget-options.model';
import { OrganizationsState } from 'src/app/organizations/state/organizations.state';
import { filter, takeUntil, flatMap, take } from 'rxjs/operators';
import { Subject, Observable, of, BehaviorSubject } from 'rxjs';
import { ClipboardService } from 'src/app/shared/clipboard.service';
import { StoryType } from 'src/app/story/models/story-type.enum';
import { HttpErrorResponse } from '@angular/common/http';
import { DialogService } from 'src/app/dialogs/dialog.service';
import { PublishState } from 'src/app/publish/state/publish.state';
import { get } from 'lodash';
import { BookmarksService } from '../../bookmarks.service';
import { ExploreService } from 'src/app/explore/explore.service';

export enum BookmarkActionsEnum {
  View = 'view',
  Edit = 'edit',
  Manage = 'manage',
  Embed = 'embed',
  Delete = 'delete',
  CopyLink = 'copy-link',
  CopyBookmark = 'copy-bookmark',
  CopyTeamBookmark = 'copy-team-bookmark'
}

export interface OtherAction {
  label: string;
  value: any;
}

@Component({
  selector: 'app-bookmark-actions',
  templateUrl: './bookmark-actions.component.html',
  styleUrls: ['./bookmark-actions.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BookmarkActionsComponent implements OnInit, OnDestroy {
  bookmarkActionsEnum = BookmarkActionsEnum;

  @Input()
  public get bookmark(): BookmarkModel {
    return this._bookmark;
  }
  public set bookmark(value: BookmarkModel) {
    if (value == null) {
      return;
    }
    
    this.bookmarkSubject.next(value);
  }
  private _bookmark: BookmarkModel;

  private bookmarkSubject = new BehaviorSubject<BookmarkModel>(null);

  @Input()
  bookmarkId: string;

  @Input()
  bookmarkType: StoryType = StoryType.Module;

  @Input()
  bookmarkEmbedTypes: StoryType[] = [StoryType.Bookmark];

  @Input()
  theme: 'light' | 'dark' = 'dark';

  @Input()
  bookmarkActions: BookmarkActionsEnum[] = [
    BookmarkActionsEnum.View,
    BookmarkActionsEnum.Edit,
    BookmarkActionsEnum.Manage,
    BookmarkActionsEnum.Embed,
    BookmarkActionsEnum.Delete,
    BookmarkActionsEnum.CopyLink
  ];

  @Input()
  otherActions: OtherAction[] = [];

  @Output()
  otherClick = new EventEmitter<any>();

  private ngUnsubscribe = new Subject();

  constructor(
    private dialog: MatDialog,
    private dialogService: DialogService,
    private store: Store,
    private actions: Actions,
    private snackbar: MatSnackBar,
    private clipboardService: ClipboardService,
    
    private bookmarksService: BookmarksService,
    private exploreService: ExploreService,

    private changeDetectorRef: ChangeDetectorRef
  ) { }

  ngOnInit() {
    this.bookmarkSubject
      .pipe(
        filter(bookmark => {
          return bookmark != null;
        }),
        takeUntil(this.ngUnsubscribe),
      )
      .subscribe(bookmark => {
        this._bookmark = bookmark;
        this.bookmarkId = bookmark.id;
        this.bindApplicationEvents();

        this.changeDetectorRef.markForCheck();
      });
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
  
  bindApplicationEvents() {
    // handle delete bookmark/favorite
    this.actions
      .pipe(
        ofActionDispatched(DeleteBookmarkSuccess, DeleteFavoriteSuccess),
        filter((action: DeleteBookmarkSuccess) => action.bookmarkId === this.bookmark.id),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(() => {
        this.handleDeleteSuccess();
      });

    this.actions
      .pipe(
        ofActionDispatched(DeleteBookmarkFail, DeleteFavoriteFail),
        filter((action: DeleteBookmarkFail) => action.bookmarkId === this.bookmark.id),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(() => {
        this.handleDeleteFailure();
      });

    // Copy generated link to clipboard
    this.actions
      .pipe(
        ofActionDispatched(GetPublishURLSuccess),
        filter(() => !this.store.selectSnapshot(PublishState.getPublishing)),
        filter((action: GetPublishURLSuccess) => action.model.id === this.bookmark.id),        
        flatMap((response: GetPublishURLSuccess) => this.copyToClipboard(response.url)),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((copied) => {
        this.snackbar.open(copied ? 'Successfully copied link to clipboard.' : 'Unable to copy link to clipboard.');
      });

    this.actions
      .pipe(
        ofActionDispatched(GetPublishURLFail),
        filter((action: GetPublishURLFail) => action.model.id === this.bookmark.id),
        takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.snackbar.open('Unable to generate URL.');
      });

    this.actions
      .pipe(
        ofActionDispatched(CopyBookmarkSuccess),
        filter((action: CopyBookmarkSuccess) => action.sourceId === this.bookmark.id),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((e) => {
        this.handleCopySuccess(e);
      });

    this.actions
      .pipe(
        ofActionDispatched(CopyBookmarkFailure),
        filter((action: CopyBookmarkFailure) => action.id === this.bookmark.id),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((action) => {
        this.handleCopyFailure(action.error);
      });
  }

  handleDeleteSuccess() {
    this.snackbar.open('Successfully deleted 3D model');
  }

  handleDeleteFailure() {
    this.snackbar.open('There was an error deleting the 3D model');
  }

  handleManageClick() {
    this.dialogService.openManageDialog(this.bookmark);
  }

  handleMenuOpened() {
    if (!this.bookmarkSubject.value) {
      if (this.bookmarkType === StoryType.Module) {
        this.exploreService.getConditionModel(this.bookmarkId)
          .subscribe(bookmark => {
            this.bookmarkSubject.next(bookmark);
            this.bookmarkSubject.complete();
          });
      } else {
        this.bookmarksService.getBookmarkModel(this.bookmarkId)
          .subscribe(bookmark => {
            this.bookmarkSubject.next(bookmark);
            this.bookmarkSubject.complete();
          });
      }
    }
  }

  handleDeleteClick() {
    this.delete();
  }

  handleCopySuccess({ groupAccess, collection }: CopyBookmarkSuccess) {
    const title = this.bookmark.title;

    if (collection) { // copied to collection
      this.snackbar.open(`${title} was successfully added to the "${collection.collection_name}" collection.`);
    } else if (groupAccess) { // copied to group
      this.snackbar.open(`${title} was successfully added to Team Models.`);
    } else { // default
      this.snackbar.open(`${title} was successfully added to My Models`);
    }
  }

  handleCopyFailure(error: HttpErrorResponse) {
    const message = get(error, 'error.error.message');

    switch (message) {
      case 'Action forbidden.  Your account content limit has been reached':
        const dialog$ = this.dialogService.openLimitDialog();

        dialog$.afterClosed()
          .subscribe(result => {
            if (result) {
              this.dialogService.openUpgradeDialog();
            }
          });

        break;
      default: 
        this.snackbar.open('There was an error adding to My Models');
    }

  }

  handleEmbedModel() {
    this.dialog.open(PublishDialogComponent, {
      width: '90vw',
      height: '95vh',
      data: <PublishDialogData>{
        bookmark: this.bookmark
      }
    });
  }

  handleCopyLinkClick() {
    this.copyLink();
  }

  handleCopyBookmarkClick() {
    this.copyBookmark();
  }
  
  handleAddTeamModelClick() {
    this.dialogService.openAddTeamModelDialog(this.bookmark);
  }

  handleOtherClick(action: OtherAction) {
    this.otherClick.emit(action);
  }
  
  copyToClipboard(copyText: string): Observable<boolean> {
    return this.clipboardService.copyToClipboard(copyText)
      .pipe(
        flatMap((copied) => {
          if (copied) {
            return of(copied);
          } else {
            return this.dialogService.openClipboardDialog(copyText)
              .afterClosed();
          }
        })
      );
  }

  hasActions(...actions: BookmarkActionsEnum[]) {
    return actions.some((action) => this.hasAction(action));
  }

  hasAction(action: BookmarkActionsEnum) {
    const enabled = this.bookmarkActions.some((_action) => _action === action);

    if (enabled) {  // handle secondary validation
      switch (action) {
        case BookmarkActionsEnum.Delete:
          return (this.bookmark && this.bookmark.is_owner) || this.bookmark.favorite;
        case BookmarkActionsEnum.Embed:
          return this.bookmarkEmbedTypes.includes(this.bookmarkType);
        case BookmarkActionsEnum.CopyBookmark:
          return this.bookmark && !this.bookmark.is_owner && !this.bookmark.favorite && !this.bookmark.unavailable;
        default:
          return enabled;
      }
    } else {
      return enabled;
    }
  }

  delete() {
    const dialogRef = this.dialogService.openConfirmDialog({
      data: {
        titleText: 'Delete 3D model',
        bodyText: 'Are you sure you want to delete this 3D model?',
        confirmText: 'Delete',
        confirmColor: 'warn'
      }
    });

    dialogRef.afterClosed()
      .pipe(
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(confirmed => {
        if (confirmed) {
          // special case: favorites
          const isFavorite = this.bookmark.favorite;
          if (isFavorite) {
            this.store.dispatch(new DeleteFavorite(this.bookmark));
          } else {
            this.store.dispatch(new DeleteBookmark(this.bookmarkId));
          }
        }
      });
  }

  copyLink() {
    // default export options
    const widgetOptions = this.store.selectSnapshot(OrganizationsState.getWidgetOptions) || new WidgetOptions();
    this.store.dispatch(
      new GetPublishURL(this.bookmark, widgetOptions)
    );
  }

  copyBookmark() {
    this.store.dispatch(new CopyBookmark(this.bookmarkId, this.bookmarkType));
  }
  
}
